import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { LastVisitedAPI } from '@chancenwerk/campus-learning-content/api/LastVisitedAPI.js';
import '@chancenwerk/components/notifications/chancen-component-notification-list.js';
import { Course } from '@chancenwerk/db/courses/Course.js';
import { CourseDigital } from '@chancenwerk/db/courses/CourseDigital.js';
import { StudentType } from '@chancenwerk/db/enums/enums.js';
import { CourseFilter } from '@chancenwerk/db/filter/CourseFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-icon.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { CWPollNotification } from '@chancenwerk/poll/client/cw-poll-notification.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { switchMap } from 'rxjs';
import gradHat from '../assets/icons/graduate_hat_filled.svg';
import arrow from '../assets/icons/play.svg';
import '../components/campus-component-section-lesson-list.js';
import { CampusRoutes } from './../routes.js';
import './campus-page-no-permission.js';

@customElement('campus-student-dashboard')
export class CampusStudentDashboard extends ChemistryLitElement {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());
  @state() private readonly _digitalCourses = new RXController(
    this,
    this._currentUser.observable.pipe(
      switchMap(async user => {
        if (!user) {
          return [];
        }

        return Course.getCourseForUser(
          user._id,
          new CourseFilter({ courseType: CourseDigital._class }),
        )({
          _id: 1,
          _class: 1,
        });
      }),
    ),
  );
  @state() private readonly _lastVisited = new RXController(
    this,
    LastVisitedAPI.get()({
      lessons: {
        name: 1,
      },
      diagnostics: {
        lessonID: 1,
        ratio: 1,
      },
    }),
  );

  private readonly size = new WindowSizeController(this);

  override styles = () => [
    {
      ':host': {
        // height: '100%',
        width: '100%',
      },
    },
  ];

  override render() {
    return permissionGuard(ChancenPermission.campus.READ_CONTENT, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="lg">
          <cr-h2> Mein Bereich </cr-h2>
          <cr-text>
            Hallo ${this._currentUser.value?.vorname}! 👋 Willkommen in deinem ganz persönlichen Bereich. Hier findest
            du die wichtigsten Funktionen und Neuigkeiten. Beispielsweise kannst du hier deinen Fortschritt verfolgen
            und direkt zu den für dich ausgewählten Aufgaben springen.
          </cr-text>
        </cr-flex>

        <cr-flex horizontal space="lg" wrap ${css({ alignItems: 'start' })}>
          ${this._coursesBox()} ${this._lessonsBox()} ${this._notificationsBox()} ${this._pollBox()}
        </cr-flex>
        <cr-flex w100 ${css({ height: '100px' })}></cr-flex>
      </cr-flex>
    `;
  }

  private _notificationsBox() {
    if (this._currentUser.value?.email.includes('vidisdummy')) {
      return nothing;
    }

    return html`
      <cr-card space="lg" padding="md" flex ${css({ minWidth: '300px' })}>
        <cr-h3> Neuigkeiten </cr-h3>

        <chancen-component-notification-list .limit=${3}></chancen-component-notification-list>
        <cr-flex crossaxis-start>
          <cr-button href=${CampusRoutes.allNotifications}> Alle Neuigkeiten </cr-button>
        </cr-flex>
      </cr-card>
    `;
  }

  private _pollBox() {
    if (this._currentUser.value?.email.includes('vidisdummy')) {
      return nothing;
    }

    return html`
      <cr-card space="lg" padding="md" flex ${css({ minWidth: '300px' })}>
        <cr-h3> Umfragen </cr-h3>

        ${CWPollNotification()}
      </cr-card>
    `;
  }

  private _lastVisitedBox() {
    if (!this._lastVisited.value?.lessons?.length) {
      return nothing;
    }

    return html` <cr-flex space="md">
        <cr-h3> Zuletzt genutzte Lerneinheiten </cr-h3>
        <cr-text> Hier kannst du zu deinen zuletzt genutzten Lerneinheiten springen. </cr-text>
      </cr-flex>
      <cr-card>
        <cr-grid columns="max-content 1fr max-content max-content" space="sm" ${css({ alignItems: 'center' })}>
          ${this._lastVisited.value?.lessons?.map(l => {
            return html`<img src="${gradHat}" alt="" ${css({ width: '20px' })} />
              <cr-text bold>${l.name}</cr-text>
              <cr-progress-bar
                ${css({ width: '100%' })}
                .value=${this._lastVisited.value?.getDiagnosticForLessonID(l._id)?.ratio ?? 0}
              ></cr-progress-bar>
              <cr-button
                small
                mode="fab"
                over-animation="jump"
                icon=${arrow}
                @click=${() => goTo(`${CampusRoutes.play}/${l._id}`)}
              >
              </cr-button>`;
          })}
        </cr-grid>
      </cr-card>`;
  }

  private _lessonsBox() {
    return html`
      <cr-flex space="lg" horizontal flex ${css({ minWidth: '300px' })}>
        <cr-flex flex>
          <cr-card space="lg" padding="md">
            ${this._lastVisitedBox()}
            <cr-h3> Deine Lektionen </cr-h3>
            <campus-component-section-lesson-list
              .mode=${'play'}
              .selector=${'assigned'}
              .showSearchBar=${false}
              condensed
            ></campus-component-section-lesson-list>
            <cr-flex crossaxis-start>
              <cr-button href=${CampusRoutes.allLessons}> ALLE LEKTIONEN </cr-button>
            </cr-flex>
          </cr-card>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _coursesBox() {
    if (this._currentUser.value instanceof Student) {
      if (
        this._currentUser.value.schuelertyp === StudentType.US ||
        (this._digitalCourses.value ?? []).length > 0 ||
        this._currentUser.value.email.includes('vidisdummy')
      ) {
        return nothing;
      }
    } else {
      return nothing;
    }

    return html`
      <cr-flex space="lg" horizontal w100>
        <cr-flex flex>
          <cr-flex space="lg" padding="md" ${css({ backgroundColor: 'white', borderRadius: '15px' })}>
            <cr-h3> Digitale Intensivkurse </cr-h3>
            <cr-text>Hast du schon deinen digitalen Intensivkurs ausgewählt?</cr-text>
            <cr-flex crossaxis-start>
              <cr-button href=${CampusRoutes.chooseDigitalCourses}> ANMELDUNG </cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }
}
